import { Injectable } from '@angular/core';
import { IProduct } from '@core/product/models/product.model';
import { IProductInfo } from '@core/product/models/product-info.model';
import {
  INumberIncrementProductItem,
  INumberQtyChange
} from '@core/form/number-increment/number-increment-product-item.model';
import { OrderItemService } from '@core/order/order-item.service';
import { DateFormatService } from '@roller/rdl/utilities/date';
import { IProductSession, TWhenAttendingProductSession } from '@core/product/models/product-session.model';
import { IOrderItem } from '@core/order/models/order-item.model';
import { IWhenAttending } from '@app/when-attending/when-attending.model';
import { IOrderItemCompulsoryChangesOptions, IOrderItemCreateOptions } from '@core/order/models/order.service.model';
import { ProductAvailableService } from '@core/product/product-available.service';

/**
 * This service is responsible for calculating and returning products/addons that should be added
 * to the order automatically when an item is added
 */
@Injectable({
  providedIn: 'root'
})
export class OrderCompulsoryTicketService {
  constructor(
    private orderItemService: OrderItemService,
    private dateService: DateFormatService,
    private productAvailableService: ProductAvailableService
  ) {}

  /**
   * Determines if every compulsory ticket is in the basket for the selected session, with the correct min purchase
   * @param compulsoryTicketIds product ids that are required to be in the basket
   * @param orderItems filtered to cart items with same relatedOrderItemKey
   * @returns true if every compulsory ticket is in the cart
   */
  isEveryCompulsoryTicketInBasket(compulsoryTicketIds: string[], orderItems: IOrderItem[]): boolean {
    return compulsoryTicketIds.every((compulsoryTicketId: string) => {
      const orderItem = orderItems.find((oi: IOrderItem) => oi.productId === compulsoryTicketId);
      if (!orderItem) {
        return false;
      }

      return orderItem.quantity >= orderItem.product.minPurchase;
    });
  }

  /**
   * Checks the current order item list and product tickets to determine if there are any forced or sibling tickets.
   * @returns [compulsoryTicketParents[], compulsoryTickets[]]
   */
  checkPresenceCompulsoryTickets(
    productInfo: IProductInfo[],
    checkoutBasket: IOrderItem[],
    selectedDate: IWhenAttending | undefined,
    selectedSession: TWhenAttendingProductSession | undefined
  ): [string[], string[]] {
    let compulsoryTicketParents: string[] = [];
    let compulsoryTickets: string[] = [];

    const productHasCompulsoryTickets = productInfo.some((info: IProductInfo) => info.forceMinPurchase);

    if (productHasCompulsoryTickets) {
      // tickets that don't require a minimum purchase are considered 'parents'
      compulsoryTicketParents = productInfo
        .filter((info: IProductInfo) => !info.forceMinPurchase)
        .map((item: IProductInfo) => item.id);

      // tickets that require a minimum purchase (compulsory)
      compulsoryTickets = productInfo
        .filter((info: IProductInfo) => info.forceMinPurchase)
        .map((item: IProductInfo) => item.id);

      const compulsoryTicketIdsInBasket: string[] = this.filterCompulsoryItemsInBasket(
        checkoutBasket,
        compulsoryTickets,
        selectedDate?.date,
        selectedSession as IProductSession //only regular packages don't use IProductSession, and they can't have forced tickets
      );

      // every forced sibling already in checkoutBasket for same date/session
      if (compulsoryTickets.every((id: string) => compulsoryTicketIdsInBasket.includes(id))) {
        return [[], []];
      }
    }

    return [compulsoryTicketParents, compulsoryTickets];
  }

  /** Filters the checkout basket against the compulsory Tickets */
  private filterCompulsoryItemsInBasket(
    basket: IOrderItem[],
    compulsoryTickets: string[],
    selectedDate: Date | undefined,
    selectedSession: IProductSession | undefined
  ): string[] {
    return basket
      .filter((order: IOrderItem) => {
        if (!compulsoryTickets.includes(order.productId)) {
          return false;
        }

        if (order.product.isItemDateBased && !this.dateService.isSameDay(order.bookingDate, selectedDate)) {
          return false;
        }

        if (
          order.product.isItemSessionBased &&
          (!selectedSession ||
            order.session?.startTime !== selectedSession.startTime ||
            order.session?.endTime !== selectedSession.endTime)
        ) {
          return false;
        }

        return order.quantity >= order.product.minPurchase;
      })
      .map((item: IOrderItem) => item.productId);
  }

  /** When an order item is changed, determine what changes need to be reflected in the order based on compulsory ticket rules */
  getCompulsoryTicketChanges(options: IOrderItemCompulsoryChangesOptions): IOrderItem[] {
    const productsCompulsoryItems = options.change.parentProduct.products.filter(
      (product: IProductInfo) => product.forceMinPurchase
    );
    if (!productsCompulsoryItems?.length) {
      return [];
    }

    // check if every compulsory ticket is in the basket
    const compulsoryTicketIds = productsCompulsoryItems.map((product: IProductInfo) => product.id);
    const allCompulsoryItemsInBasket = this.isEveryCompulsoryTicketInBasket(
      compulsoryTicketIds,
      options.relatedOrderItems
    );
    if (allCompulsoryItemsInBasket) {
      return [];
    }

    const orderItem = options.changedItem;
    orderItem.isCompulsoryPeer = !orderItem.product.forceMinPurchase;

    const compulsoryItems: IOrderItem[][] = productsCompulsoryItems.map((compulsoryProduct: IProductInfo) => {
      const ticket = orderItem.parentProduct.products.find((item: IProductInfo) => item.id === compulsoryProduct.id);

      return this.getAllCompulsoryTicketChanges(options, compulsoryProduct.id, ticket);
    });

    // User could have added the compulsory item first - filter it out so not added twice
    return compulsoryItems.flat().filter((item) => item.productId !== orderItem.productId);
  }

  /** When compulsory tickets are present, find if their tickets are available and add them to the cart or update their quantity */
  private getAllCompulsoryTicketChanges(
    options: IOrderItemCompulsoryChangesOptions,
    compulsoryProductId: string,
    ticket: IProductInfo | undefined
  ): IOrderItem[] {
    if (!ticket) {
      return [];
    }

    const parentProduct = options.change.parentProduct;
    const selectedSession = options.changedItem.session;
    const bookingDate = options.changedItem.bookingDate;

    // If we can't add this ticket, skip
    const canAddProduct = this.productAvailableService.canAddProduct(
      ticket,
      options.order.items,
      bookingDate,
      selectedSession,
      !!options.order.existingBooking
    );
    if (!canAddProduct) {
      return [];
    }

    // Related order items is already filtered by key, so we are just finding existing product
    const existingOrderItem: IOrderItem | undefined = options.relatedOrderItems.find(
      (relatedItem: IOrderItem) => relatedItem.productId === compulsoryProductId
    );
    const compulsoryOrderItems: IOrderItem[] = [];

    if (!existingOrderItem) {
      // they don't have quantity add them to the basket with min quantity as well
      const change: INumberQtyChange = {
        ev: {
          quantity: ticket.minPurchase,
          product: ticket,
          id: compulsoryProductId,
          additionalGuests: 0
        },
        parentProduct,
        selectedSession
      };
      const associatedBasketItem =
        options.relatedOrderItems.find((relatedItem: IOrderItem) => !relatedItem.addonToProductId) ||
        options.changedItem;

      const createOptions: IOrderItemCreateOptions = {
        change,
        relatedOrderItemKey: options.relatedOrderItemKey,
        bookingDate,
        isAddon: options.isAddon,
        relatedOrderItems: [associatedBasketItem],
        parentUniqueId: associatedBasketItem.uniqueId
      };

      const compulsoryItem = this.orderItemService.createOrderItem(createOptions);

      compulsoryItem.isCompulsoryPurchase = true;
      compulsoryOrderItems.push(compulsoryItem);
    } else if (existingOrderItem?.quantity && existingOrderItem.quantity < existingOrderItem.product.minPurchase) {
      // updates the added item quantity to the minimum purchase required
      // this covers the scenario where they have already added the item to the cart by itself
      // but adding a sibling ticket has now made it min purchase compulsory
      existingOrderItem.quantity = ticket.minPurchase;
      compulsoryOrderItems.push(existingOrderItem);
    }

    return compulsoryOrderItems;
  }

  /**
   * Creates forced purchase addon order items for give order item and basket
   * Adds each addon ticket with the total quantity of all order items for the selected product
   * @returns Array of oder items that are addons of the current order item
   */
  getForcedPurchaseBasket(
    minForcedAddonCount: number,
    orderItem: IOrderItem,
    checkoutBasket: IOrderItem[],
    forcedPurchaseAddons: IProduct[],
    relatedOrderItemKey: string,
    bookingDate: Date,
    session?: IProductSession
  ): IOrderItem[] {
    if (!minForcedAddonCount) {
      return [];
    }

    const forcedAddons: IOrderItem[] = [];
    forcedPurchaseAddons.forEach((addon: IProduct) => {
      addon.products.forEach((addonTicket: IProductInfo) => {
        if (addonTicket.hasUserDefinedCost) {
          return;
        }

        let addonOrderItem = checkoutBasket.find(
          (basketItem) =>
            basketItem.relatedOrderItemKey === relatedOrderItemKey &&
            basketItem.productId === addonTicket.id &&
            this.dateService.isSameDay(basketItem.bookingDate, bookingDate)
        );

        const amountRemaining = this.productAvailableService.getMaxAddAmount(
          addonTicket,
          checkoutBasket,
          bookingDate,
          session,
          false
        );
        const maxAmount = amountRemaining + (addonOrderItem?.quantity || 0);
        const quantity = Math.min(minForcedAddonCount, maxAmount);
        if (addonOrderItem) {
          addonOrderItem.quantity = quantity;
        } else {
          addon.hideSessionDuration = orderItem.hideSessionDuration;
          const product: INumberIncrementProductItem = {
            id: addonTicket.id,
            quantity,
            product: addonTicket,
            additionalGuests: 0
          };

          addonOrderItem = this.orderItemService.buildAddonOrderItem(
            product,
            addon,
            bookingDate || orderItem.bookingDate,
            session,
            orderItem,
            true,
            relatedOrderItemKey
          );
        }

        forcedAddons.push(addonOrderItem);
      });
    });

    return forcedAddons;
  }

  /** taking into account current basket changes and the related basket items the minimum the related forced addons should be. */
  calculateMinForcedAddon(orderItems: IOrderItem[], relatedBasketItems: IOrderItem[]): number {
    const orderItemUniqueIds = orderItems.map((item: IOrderItem) => item.uniqueId);
    const filterRelatedToExcludeChanges = relatedBasketItems.filter(
      (item: IOrderItem) => !orderItemUniqueIds.includes(item.uniqueId)
    );
    // ensure this also calculates the current related items in the basket...
    return [...orderItems, ...filterRelatedToExcludeChanges].reduce((acc: number, orderChange: IOrderItem) => {
      if (!orderChange.addonToProductId) {
        acc += orderChange.quantity;
      }

      return acc;
    }, 0);
  }
}
